import { ActionsBase, AxialRotationEnum, BoneTypeEnum, CaseModel, SelectedApexMech, State as StateBase, StateDescription as StateDescriptionBase, StateTransitionData, ViewType } from "@ortho-next/nextray-core";
import { ScrewList } from "../../app/core";
import { LayerElements, PlateOrientationType } from "../Models/AppModel";
import { BoneTypeMinEnum } from "../Models/Enums";
import { PlateModel } from "../Models/PlateModel";
import { Context } from "./Context";

/**
 * List of context events.
 */
export interface Actions extends ActionsBase<LayerElements> { // TODO fix bridgeresult?
	/**
	 * Sets the initial state based on the case model.
	 */
	setInitialState: CaseModel;
	/**
	 * Starts insert mechanical axis workflow.
	 */
	insertMechanicalAxis: ViewType;
	/**
	 * Starts insert contralateral mechanical axis workflow.
	 */
	insertControlaterMechanicalAxis: null;
	/**
	 * Starts the bone type selection workflow.
	 */
	startBoneTypeSelection: null;
	/**
	 * Ends bone type selection workflow without confirm.
	 */
	cancelBoneTypeSelection: null;
	/**
	 * Confirm bone type selection and ends workflow.
	 */
	confirmBoneTypeSelection: BoneTypeEnum;
	/**
	 * Set insert selected apex during ostoetomy workflow.
	 */
	selectApex: SelectedApexMech;
	/**
	 * Sets osteotomy cut.
	 */
	osteotomyCut: OsteotomyCut;
	/**
	 * Ends insert osteotomy workflow.
	 */
	confirmApex: null;
	/**
	 * Sets standard angles to calculate apex.
	 */
	setStandardAngle: SetStandardAngleParams;
	/**
	 * Adds or removes the plane cut along the osteotomy.
	 */
	cutEoC: boolean;
	/**
	 * Inserts plate to the scenes.
	 */
	insertPlate: PlateModel;
	/**
	 * Sets the axial rotation angle degrees for a specific bone.
	 */
	setAxialRotation: SetAxialRotation;
	/**
	* Sets the axial rotation angle sign for a specific bone.
	*/
	setAxialRotationSign: SetAxialRotationSign;
	/**
	 * Starts select osteotomy workflow.
	 */
	startSelectingApex: null;
	/**
	 * Ends select apex workflow and restore old selected apex value.
	 */
	cancelSelectingApex: null;
	/**
	 * Switches the current state to calibrated.
	 */
	switchToCalibrated: null;
	/**
	 * Switches the current state to deformity.
	 */
	switchToDeformity: null;
	/**
	 * Switches the current state to EOC.
	 */
	switchToEOC: null;
	/**
	 * Switches the current state to templating.
	 */
	switchToTemplating: null;
	/**
	 * Switches the current state to RPM.
	 */
	switchToRPM: null;
	/**
	 * Rotate camera based on current state.
	 */
	rotateCamera: null;
	/**
	 * Starts the inserting plate workflow.
	 */
	startInsertingPlate: null;
	/**
	 * Ends inserting plate workflow.
	 */
	confirmPlate: null;
	/**
	 * Ends inserting plate workflow and restore old selected plate.
	 */
	cancelPlate: null;
	/**
	 * Flips the plate.
	 */
	flipPlate: PlateOrientationType;
	/**
	 * Generates images for print report.
	 */
	generateImagesToPrint: null;
	/**
	 * Updates measures available for print report only.
	 */
	updateMeausuresOnlyPrint: null;
	/**
	* Update screw list.
	*/
	updateScrews: ScrewList;
}

export interface SetAxialRotation {
	value: number;
	boneType: BoneTypeMinEnum;
}

export interface SetAxialRotationSign {
	value: AxialRotationEnum;
	boneType: BoneTypeMinEnum;
}

export interface SetStandardAngleParams {
	angle: CustomStandardAngles;
	value: number;
}

export enum CustomStandardAngles {
	LPFA,
	mLDFA,
	MPTA,
	LDTA,
	PPTA,
	ADTA
}

export enum RegisteringType {
	mechanicalAxisAP,
	mechanicalAxisLT,
	contralateral
}

export enum OsteotomyCut {
	none,
	opening,
	closing
}

export enum PrintStateTypes {
	none,
	deformityAnalysis,
	defAnalysisNext,
}

/**
 * Workflow list.
 */
export enum WorkflowRunning {
	none,
	insertMechanicalAxis,
	insertContralateral,
	crop,
	rotate,
	insertOsteotomy,
	insertPlate,
	boneTypeSelection,
}

/**
 * Data of current state.
 */
export interface StateDescription extends StateDescriptionBase {
	isWorkflowRunning: WorkflowRunning; //override
}

/**
 * Abstract class to create a state.
 */
export abstract class State extends StateBase<Actions> {
	public abstract override stateDescription: StateDescription; //todo StateDescription override
	protected abstract override handleChangeStateEvent<K extends keyof Actions>(event: K, args: Actions[K]): StateTransitionData;
	protected declare _context: Context;
	protected declare _caseModel: CaseModel;

	public override onAfterInit(): void {
		super.onAfterInit();
		this.handle("rotateCamera", null);
	}

	protected override handleNotChangeStateEvent<K extends keyof Actions>(event: K, args: Actions[K]): StateTransitionData {
		switch (event) {
			case "rotateCamera":
				this.dispatchToMain(event, args);
				return { type: this.type };
		}
		return super.handleNotChangeStateEvent(event, args);
	}
}
